/**
 * @module utils/chartLegend
 * @description 图例颜色工具函数 — 从 ChartsDrop/components/LegendColorSetting/utils 提取
 *
 * 用途：
 * - seriesUtils.ts 引用 buildLegendPaletteIndexMap、findRgbaForLegend、setRgbaAlpha
 *
 * 这些函数是纯工具函数，不依赖 ChartsDrop 内部状态
 *
 * @see docs/DVA环境迁移执行计划.md §N7
 */

import { LIGHT_RGBA_COLORS } from '@customComp/Chart/_config/_constant';
import type { TLegendColors } from '@/pages/datalab/pages/Multidimensional/Analysis/components/ChartSettingsV2/settingGroups//LegendColorSetting/types';

/**
 * 在 draft 中按 name 查找颜色（rgba），无则返回默认 rgba（来自 LIGHT_RGBA_COLORS）。
 *
 * @param name - 图例名称
 * @param index - 默认颜色索引
 * @param draftColors - 用户自定义颜色 draft 映射
 * @returns rgba 字符串
 */
export function findRgbaForLegend(
  name: string,
  index: number,
  draftColors: TLegendColors | undefined,
): string {
  const found = draftColors?.[name];
  if (found) return found;
  return LIGHT_RGBA_COLORS[index % LIGHT_RGBA_COLORS.length] ?? 'rgba(80, 134, 243, 1)';
}

/**
 * 统一的 legendKey 生成规则（与图表渲染侧保持一致）。
 * - 有对照组：`${controlName},${seriesDataName}`
 * - 无对照组：`${seriesDataName}`
 *
 * @param params - { controlName, seriesDataName }
 * @returns legendKey 字符串
 */
export function buildLegendKey(params: {
  controlName: string;
  seriesDataName: string;
}): string {
  const { controlName, seriesDataName } = params;
  if (controlName) {
    return `${controlName},${seriesDataName || ''}`;
  }
  return seriesDataName || '';
}

/**
 * 将 legendKey 的有序列表转换成稳定的 paletteIndex 映射（取首次出现的下标）。
 * 这样在"对比时间重复 series/去重展示"等场景下，两端默认色索引一致且不跳色。
 *
 * @param legendKeysInOrder - 按顺序排列的 legendKey 列表
 * @returns legendKey → paletteIndex 映射
 */
export function buildLegendPaletteIndexMap(
  legendKeysInOrder: string[],
): Record<string, number> {
  const map: Record<string, number> = {};
  legendKeysInOrder.forEach((key, index) => {
    if (!key) return;
    if (!Number.isFinite(map[key])) {
      map[key] = index;
    }
  });
  return map;
}

/**
 * 设置 rgba/rgb 字符串的透明度（alpha）。
 *
 * @param rgba - rgba 或 rgb 字符串
 * @param alpha - 透明度值 (0-1)
 * @returns 修改后 alpha 的 rgba 字符串
 */
export function setRgbaAlpha(rgba: string, alpha: number): string {
  const nextAlpha = clampNum(alpha, 0, 1);
  const input = String(rgba || '');
  if (input.startsWith('rgba(')) {
    return input.replace(/[\d.]+(?=\))/, () => nextAlpha.toString());
  }
  if (input.startsWith('rgb(')) {
    return input.replace(/^rgb\(/, 'rgba(').replace(/\)$/, `, ${nextAlpha})`);
  }
  return input;
}

// ── 内部工具 ──

function clampNum(v: number, min: number, max: number): number {
  const n = Number.isFinite(v) ? v : min;
  return Math.min(max, Math.max(min, n));
}
